import React, { useEffect, useRef, useState } from 'react';
import { DragSource, DragTarget, useDragAndDropContext } from './bonds-page/drag-and-drop-context.tsx';
import useDealBuilderResultStore from '../../stores/deal-builder-result.store.ts';
import { DealBuilder } from '../services/deal-builder.ts';
import { AccountBalance, Add, AutoGraph, Bolt, ContentPaste, Edit, History, Lock, LockOpen, Percent, ShowChart, Tune } from '@mui/icons-material';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Menu, MenuItem, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import SerialBondRow from './production-page/serial-bond-row.tsx';
import TermBondPrincipalRow from './production-page/term-bond-principal-row.tsx';
import { TermBond } from './debt-service-page.tsx';
import formatter from '../../tools/formatter.js';
import useButtonSwitching from '../hooks/useButtonSwitching.ts';
import _ from "lodash";
import { CSVLink } from "react-csv/lib";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CurrencyInput from './bonds-page/currency-input.tsx';
import NewTicDisplay from './shared/new-tic-display.tsx';
import DealBuilderSaverUtils from '../utils/deal-builder-saver.utils.ts';

interface ProductionPageProps {
  builder: DealBuilder
}

export default function ProductionPage({ builder }: ProductionPageProps) {
  const { subscribe } = useDragAndDropContext();
  const { setDealBuilderService, resetCopy } = useDealBuilderResultStore();
  const [bulkPastingYield, setBulkPastingYield] = useState<boolean>(false);
  const [bulkPastingYieldValue, setBulkPastingYieldValue] = useState<string>("");
  const [editingAllCouponsValue, setEditingAllCouponsValue] = useState<number>(0.05);
  const [editingAllYieldsValue, setEditingAllYieldsValue] = useState<number>(0.05);
  const [yieldButtonAnchor, setYieldButtonAnchor] = useState<null | HTMLElement>(null);
  const [couponsButtonAnchor, setCouponsButtonAnchor] = useState<null | HTMLElement>(null);
  const [predictionsButtonAnchor, setPredictionsButtonAnchor] = useState<null | HTMLElement>(null);
  const [principalButtonAnchor, setPrincipalButtonAnchor] = useState<null | HTMLElement>(null);
  const [editingAllCoupons, setEditingAllCoupons] = useState<boolean>(false);
  const [editingCustomYield, setEditingCustomYield] = useState<boolean>(false);
  const [editingCushion, setEditingCushion] = useState<boolean>(false);
  const [editingCushionValue, setEditingCushionValue] = useState<number>(builder.cushion);
  const totalProduction = builder.getTotalProduction();
  const csvLink = useRef();
  useButtonSwitching();

  useEffect(() => {
    const unsubscribe = subscribe(onDropChange);
    return () => { unsubscribe() };
  }, [])

  function onDropChange(source: DragSource, target: DragTarget) {
    if (source.bond.type === "serial" && target.bond.type === "serial") {
      groupSerialBonds(source.bond.maturity, target.bond.maturity);
    }
  }

  async function groupSerialBonds(maturity1: string, maturity2: string) {
    builder.groupSerialBonds(maturity1, maturity2);
    await builder.updateProduction();
    setDealBuilderService(builder);
    await builder.updateProduction(builder.bonds);
    setDealBuilderService(builder);
  }

  function reset() {
    setDealBuilderService(_.cloneDeep(resetCopy));
  }

  function onPasteYieldsButton() {
    setYieldButtonAnchor(null);

    const text = builder.bonds.map(bond => {
      const row = builder.production.find(x => x.bond === bond);
      if (!row) return "n/a"
      return ((row.curve + row.spread) * 100).toFixed(3)
    }).join("\n")

    setBulkPastingYieldValue(text);
    setBulkPastingYield(true);
  }

  function onSavePastedYieldsButton() {
    const values = bulkPastingYieldValue.split("\n").map(line => {
      const num = Number(line)
      return Number.isFinite(num) ? num / 100 : undefined;
    })

    builder.setCustomYields(values);
    setDealBuilderService(builder);
    setBulkPastingYield(false);
  }

  async function onUseSplineParPricingButton() {
    setCouponsButtonAnchor(null);
    await builder.setSplineParPricingButton();
    setDealBuilderService(builder);
  }

  async function onUpdateAllCouponsButton() {
    setEditingAllCoupons(false);

    builder.bonds.forEach(bond => {
      if (bond.lockedCoupon) return;
      bond.coupon = editingAllCouponsValue;
      bond.lockedCoupon = true;
    });

    builder.updateDebtService();
    await builder.updateProduction();
    setDealBuilderService(builder)

    await builder.updateProduction(builder.bonds);
    setDealBuilderService(builder);
  }

  async function onUpdateAllYieldsButton() {
    setEditingCustomYield(false);
    await builder.setCustomYield(editingAllYieldsValue);
    setDealBuilderService(builder);
  }


  function onUpdateCushion() {
    setEditingCushion(false);
    console.log("Setting cushion", editingCushionValue)
    builder.setCushion(editingCushionValue);
    setDealBuilderService(builder);
  }

  function lockAllPrincipals() {
    setPrincipalButtonAnchor(null);
    builder.bonds.forEach(bond => {
      if (bond.type === "serial") bond.lockedPrincipal = true;
      else bond.redemptions.forEach(r => { r.locked = true; });
    });
    setDealBuilderService(builder);
  }

  function unlockAllPrincipals() {
    setPrincipalButtonAnchor(null);
    builder.bonds.forEach(bond => {
      if (bond.type === "serial") bond.lockedPrincipal = false;
      else bond.redemptions.forEach(r => { r.locked = false; });
    });
    setDealBuilderService(builder);
  }

  function lockAllCoupons() {
    setCouponsButtonAnchor(null);
    builder.bonds.forEach(bond => { bond.lockedCoupon = true; });
    setDealBuilderService(builder);
  }

  function unlockAllCoupons() {
    setCouponsButtonAnchor(null);
    builder.bonds.forEach(bond => { bond.lockedCoupon = false; });
    setDealBuilderService(builder);
  }

  function lockAllSpreads() {
    builder.production.forEach(row => { row.locked = true; });
    setDealBuilderService(builder);
  }

  function unlockAllSpreads() {
    builder.production.forEach(row => { row.locked = false; });
    setDealBuilderService(builder);
  }

  return (
    <Stack direction="column" gap={2} width="100%" margin={2}>
      <TableContainer
        sx={theme => ({
          borderRadius: 1,
          boxShadow: theme.shadows[1],
          border: '1px solid #f4f4f4',
          backgroundColor: 'white',
          transition: 'all 0.2s ease',
          borderBottom: builder.bonds.length === 0 ? '1px solid #f4f4f4' : 0,
        })}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell colSpan={99}>
                <Stack direction="row" justifyContent="space-between" py={1} gap={2} width="100%">
                  <Typography variant="subtitle1" fontWeight="semibold" color="text.primary">
                    <Stack direction="row" gap={1}>
                      <Button
                        variant="contained"
                        color="error"
                        sx={{ color: "white" }}
                        disabled={resetCopy === undefined}
                        onClick={reset}
                      >Reset</Button>

                      <Button
                        variant="contained"
                        color="primary"
                        sx={{ color: "white" }}
                        onClick={() => (csvLink.current as any).link.click()}
                      >Export</Button>


                    </Stack>
                  </Typography>

                  <Stack direction="row" gap={2} alignItems="center">
                    {builder && (
                      <NewTicDisplay builder={builder} />
                    )}
                    <Button
                      size="small"
                      startIcon={<Add />}
                      sx={{ color: 'primary.main', '&:hover': { color: 'primary.dark' } }}
                      onClick={async () => {
                        builder.updateDebtService();
                        setDealBuilderService(builder);
                        await builder.updateProduction(builder.bonds);
                        setDealBuilderService(builder);
                      }}
                    >
                      Add Bond
                    </Button>
                  </Stack>
                </Stack>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ width: '3px', px: 0 }}></TableCell>
              <TableCell sx={{ width: '32px' }}>Term</TableCell>
              <TableCell sx={{ py: "12px" }}>Maturity</TableCell>
              <TableCell align="right">
                <Stack direction="row" justifyContent="flex-end" alignItems="center" gap={0.5}>
                  Principal
                  <IconButton size="small" onClick={e => setPrincipalButtonAnchor(e.currentTarget)} sx={{ p: 0.25 }}>
                    <MoreVertIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <Menu
                    anchorEl={principalButtonAnchor}
                    open={principalButtonAnchor !== null}
                    onClose={() => setPrincipalButtonAnchor(null)}
                  >
                    <MenuItem sx={{ p: 0 }}>
                      <Button fullWidth sx={{ px: 3, py: 1, justifyContent: 'flex-start' }} startIcon={<Lock fontSize="small" />} onClick={lockAllPrincipals}>
                        Lock all
                      </Button>
                    </MenuItem>
                    <MenuItem sx={{ p: 0 }}>
                      <Button fullWidth sx={{ px: 3, py: 1, justifyContent: 'flex-start' }} startIcon={<LockOpen fontSize="small" />} onClick={unlockAllPrincipals}>
                        Unlock all
                      </Button>
                    </MenuItem>
                  </Menu>
                </Stack>
              </TableCell>
              <TableCell align="right">
                <Stack direction="row" justifyContent="flex-end" alignItems="center" gap={0.5}>
                  Coupon
                  <IconButton size="small" onClick={e => setCouponsButtonAnchor(e.currentTarget)} sx={{ p: 0.25 }}>
                    <MoreVertIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <Menu
                    anchorEl={couponsButtonAnchor}
                    open={couponsButtonAnchor !== null}
                    onClose={() => setCouponsButtonAnchor(null)}
                  >
                    <MenuItem sx={{ p: 0 }}>
                      <Button
                        fullWidth
                        sx={{ px: 3, py: 1, justifyContent: 'flex-start' }}
                        startIcon={<ShowChart fontSize="small" />}
                        onClick={() => {
                          setCouponsButtonAnchor(null);
                          onUseSplineParPricingButton();
                        }}
                      >
                        Use Spline Par Pricing
                      </Button>
                    </MenuItem>
                    <MenuItem sx={{ p: 0 }}>
                      <Button
                        fullWidth
                        sx={{ px: 3, py: 1, justifyContent: 'flex-start' }}
                        startIcon={<Percent fontSize="small" />}
                        onClick={() => {
                          setCouponsButtonAnchor(null);
                          setEditingAllCoupons(true);
                        }}
                      >
                        Update all coupons
                      </Button>
                    </MenuItem>
                    <MenuItem sx={{ p: 0 }}>
                      <Button
                        fullWidth
                        sx={{ px: 3, py: 1, justifyContent: 'flex-start' }}
                        startIcon={<AutoGraph fontSize="small" />}
                        onClick={async () => {
                          setCouponsButtonAnchor(null);
                          await builder.setSplineCouponPrediction();
                          setDealBuilderService(builder);
                        }}
                      >
                        Use Spline Coupon Prediction
                      </Button>
                    </MenuItem>
                    <Divider />
                    <MenuItem sx={{ p: 0 }}>
                      <Button fullWidth sx={{ px: 3, py: 1, justifyContent: 'flex-start' }} startIcon={<Lock fontSize="small" />} onClick={lockAllCoupons}>
                        Lock all
                      </Button>
                    </MenuItem>
                    <MenuItem sx={{ p: 0 }}>
                      <Button fullWidth sx={{ px: 3, py: 1, justifyContent: 'flex-start' }} startIcon={<LockOpen fontSize="small" />} onClick={unlockAllCoupons}>
                        Unlock all
                      </Button>
                    </MenuItem>
                  </Menu>
                </Stack>
              </TableCell>
              <TableCell align="right">
                <Stack direction="row" justifyContent="flex-end" alignItems="center" gap={0.5}>
                  Curve
                  <IconButton size="small" onClick={e => setYieldButtonAnchor(e.currentTarget)} sx={{ p: 0.25 }}>
                    <MoreVertIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <Menu
                    anchorEl={yieldButtonAnchor}
                    open={yieldButtonAnchor !== null}
                    onClose={() => setYieldButtonAnchor(null)}
                  >
                    <MenuItem sx={{ p: 0 }}>
                      <Button
                        onClick={async () => {
                          await builder.updateYieldCurve("eod_aaa");
                          setDealBuilderService(builder);
                        }}
                        fullWidth
                        sx={{ px: 3, py: 1, justifyContent: 'flex-start' }}
                        startIcon={<History fontSize="small" />}
                      >
                        Set EOD AAA
                      </Button>
                    </MenuItem>
                    <MenuItem sx={{ p: 0 }}>
                      <Button
                        onClick={async () => {
                          await builder.updateYieldCurve("real_time_aaa");
                          setDealBuilderService(builder);
                        }}
                        fullWidth
                        sx={{ px: 3, py: 1, justifyContent: 'flex-start' }}
                        startIcon={<Bolt fontSize="small" />}
                      >
                        Set Real Time AAA
                      </Button>
                    </MenuItem>
                    <MenuItem sx={{ p: 0 }}>
                      <Button
                        onClick={async () => {
                          await builder.updateYieldCurve("ust");
                          setDealBuilderService(builder);
                        }}
                        fullWidth
                        sx={{ px: 3, py: 1, justifyContent: 'flex-start' }}
                        startIcon={<AccountBalance fontSize="small" />}
                      >
                        Set US Treasury
                      </Button>
                    </MenuItem>
                    <Divider />
                    <MenuItem sx={{ p: 0 }}>
                      <Button
                        onClick={onPasteYieldsButton}
                        fullWidth
                        sx={{ px: 3, py: 1, justifyContent: 'flex-start' }}
                        startIcon={<ContentPaste fontSize="small" />}
                      >
                        Paste Yields
                      </Button>
                    </MenuItem>
                    <Divider />
                    <MenuItem sx={{ p: 0 }}>
                      <Button fullWidth sx={{ px: 3, py: 1, justifyContent: 'flex-start' }} startIcon={<Lock fontSize="small" />} onClick={() => { setYieldButtonAnchor(null); lockAllSpreads(); }}>
                        Lock all
                      </Button>
                    </MenuItem>
                    <MenuItem sx={{ p: 0 }}>
                      <Button fullWidth sx={{ px: 3, py: 1, justifyContent: 'flex-start' }} startIcon={<LockOpen fontSize="small" />} onClick={() => { setYieldButtonAnchor(null); unlockAllSpreads(); }}>
                        Unlock all
                      </Button>
                    </MenuItem>
                  </Menu>
                </Stack>
              </TableCell>
              <TableCell align="right">Spread</TableCell>
              <TableCell align="right">
                <Stack direction="row" justifyContent="flex-end" alignItems="center" gap={0.5}>
                  {builder.cushion !== 0 ? (
                    <Tooltip title={`Cushion: ${formatter.percent(builder.cushion)}`}><>Yield*</></Tooltip>
                  ) : (
                    "Yield"
                  )}
                  <IconButton size="small" onClick={e => setPredictionsButtonAnchor(e.currentTarget)} sx={{ p: 0.25 }}>
                    <MoreVertIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <Menu
                    anchorEl={predictionsButtonAnchor}
                    open={predictionsButtonAnchor !== null}
                    onClose={() => setPredictionsButtonAnchor(null)}
                  >
                    <MenuItem sx={{ p: 0 }}>
                      <Button
                        onClick={() => {
                          setPredictionsButtonAnchor(null);
                          setEditingCushion(true);
                        }}
                        fullWidth
                        sx={{ px: 3, py: 1, justifyContent: 'flex-start' }}
                        startIcon={<Tune fontSize="small" />}
                      >
                        Update cushion
                      </Button>
                    </MenuItem>
                    <MenuItem sx={{ p: 0 }}>
                      <Button
                        onClick={() => {
                          setPredictionsButtonAnchor(null);
                          setEditingCustomYield(true)
                        }}
                        fullWidth
                        sx={{ px: 3, py: 1, justifyContent: 'flex-start' }}
                        startIcon={<Edit fontSize="small" />}
                      >
                        Update custom yield
                      </Button>
                    </MenuItem>
                    <Divider />
                    <MenuItem sx={{ p: 0 }}>
                      <Button fullWidth sx={{ px: 3, py: 1, justifyContent: 'flex-start' }} startIcon={<Lock fontSize="small" />} onClick={() => { setPredictionsButtonAnchor(null); lockAllSpreads(); }}>
                        Lock all
                      </Button>
                    </MenuItem>
                    <MenuItem sx={{ p: 0 }}>
                      <Button fullWidth sx={{ px: 3, py: 1, justifyContent: 'flex-start' }} startIcon={<LockOpen fontSize="small" />} onClick={() => { setPredictionsButtonAnchor(null); unlockAllSpreads(); }}>
                        Unlock all
                      </Button>
                    </MenuItem>
                  </Menu>
                </Stack>
              </TableCell>
              <TableCell align="right">YTM</TableCell>
              <TableCell align="right">Price</TableCell>
              <TableCell align="right">Production</TableCell>

              <TableCell align="center" sx={{ width: '48px' }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {builder.bonds.length === 0 ? (
              <TableRow>
                <TableCell colSpan={99} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  You don't have configured bonds.
                </TableCell>
              </TableRow>
            ) : (
              builder.bonds.map((bond, index) => {
                return (
                  <React.Fragment key={index}>
                    {bond.type === "serial" ? (
                      <SerialBondRow
                        key={index}
                        bond={bond}
                        builder={builder}
                      />
                    ) : (
                      <React.Fragment>
                        <TermBondPrincipalRow
                          bond={bond}
                          builder={builder}
                          header={true}
                        />

                        {bond.redemptions.map((_, index) => (
                          <TermBondPrincipalRow
                            key={index}
                            bond={bond as TermBond}
                            builder={builder}
                            header={false}
                            principalIndex={index}
                          />
                        ))}
                      </React.Fragment>
                    )}
                  </React.Fragment>
                )
              })
            )}

            <TableRow>
              <TableCell sx={{ width: '3px', px: 0 }}></TableCell>
              <TableCell sx={{ width: '32px' }}></TableCell>
              <TableCell sx={{ py: "12px", fontWeight: 600 }}>Total</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>{formatter.money(builder.getTotalPrincipal())}</TableCell>
              <TableCell align="right"></TableCell>
              <TableCell align="right"></TableCell>
              <TableCell align="right"></TableCell>
              <TableCell align="right"></TableCell>
              <TableCell align="right"></TableCell>
              <TableCell align="right"></TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                {totalProduction ? formatter.money(builder.getTotalProduction()) : "N/A"}
              </TableCell>
              <TableCell align="center"></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <CSVLink
        data={DealBuilderSaverUtils.toCSVRowsNew(builder)}
        filename="splinedata_deal_builder.csv"
        className="hidden"
        ref={csvLink}
        target="_blank"
      />

      <Dialog
        open={bulkPastingYield}
        onClose={() => setBulkPastingYield(false)}
      >
        <DialogTitle>Paste your yields here</DialogTitle>
        <DialogContent sx={{ minWidth: "400px" }}>
          <TextField
            multiline={true}
            minRows={8}
            value={bulkPastingYieldValue}
            onChange={(e) => setBulkPastingYieldValue(e.target.value)}
            sx={{ width: "100%" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkPastingYield(false)}>Cancel</Button>
          <Button onClick={onSavePastedYieldsButton}>Update</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editingAllCoupons}
        onClose={() => setEditingAllCoupons(false)}
      >
        <DialogTitle>Update all coupons</DialogTitle>
        <DialogContent sx={{ minWidth: "400px" }}>
          <CurrencyInput
            className={"switch-db-1"}
            decimalScale={3}
            size="small"
            value={editingAllCouponsValue * 100}
            endAdornment="%"
            locked={undefined}
            onLockedChange={() => { }}
            onValidChange={async (value) => {
              setEditingAllCouponsValue(value / 100);
            }}
            isValueValid={() => ({ valid: true })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingAllCoupons(false)}>Cancel</Button>
          <Button onClick={onUpdateAllCouponsButton}>Update</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editingCustomYield}
        onClose={() => setEditingCustomYield(false)}
      >
        <DialogTitle>Update all yields</DialogTitle>
        <DialogContent sx={{ minWidth: "400px" }}>
          <CurrencyInput
            decimalScale={3}
            size="small"
            value={editingAllYieldsValue * 100}
            endAdornment="%"
            locked={undefined}
            onLockedChange={() => { }}
            onValidChange={async (value) => {
              setEditingAllYieldsValue(value / 100);
            }}
            isValueValid={() => ({ valid: true })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingCustomYield(false)}>Cancel</Button>
          <Button onClick={onUpdateAllYieldsButton}>Update</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editingCushion}
        onClose={() => setEditingCushion(false)}
      >
        <DialogTitle>Cushion update</DialogTitle>
        <DialogContent sx={{ minWidth: "400px" }}>
          <CurrencyInput
            decimalScale={3}
            size="small"
            value={editingCushionValue * 100}
            endAdornment="%"
            locked={undefined}
            onLockedChange={() => { }}
            onValidChange={async (value) => {
              setEditingCushionValue(value / 100);
            }}
            isValueValid={() => ({ valid: true })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingCushion(false)}>Cancel</Button>
          <Button onClick={onUpdateCushion}>Update</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
